import React from 'react';
import Icons from './Icons';

// Moving Car Card Component
export const MovingCarCard = ({ car, isDark }) => (
  <div className={`relative overflow-hidden rounded-xl md:rounded-2xl transition-all duration-500 group hover:shadow-2xl hover:shadow-[#c9a962]/20 ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
    {/* Image Section */}
    <div className="relative h-44 sm:h-48 md:h-56 overflow-hidden">
      <img 
        src={car.image} 
        alt={car.name}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent opacity-80"></div>
      
      {/* Featured Badge */}
      {car.is_featured === 1 && (
        <div className="absolute top-2 md:top-4 left-2 md:left-4 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black text-[10px] md:text-xs font-bold px-2 md:px-3 py-0.5 md:py-1 uppercase tracking-wider rounded-full">
          Featured
        </div>
      )}
      
      {/* Price Badge */}
      <div className="absolute bottom-2 md:bottom-4 right-2 md:right-4 bg-black/80 backdrop-blur-sm text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg">
        <span className="text-base md:text-xl font-bold text-[#c9a962]">₹{car.price_per_day ? car.price_per_day.toLocaleString() : car.price_per_day}</span>
        <span className="text-[10px] md:text-sm text-gray-400">/day</span>
      </div>
    </div>
    
    {/* Content Section */}
    <div className="p-4 md:p-6">
      <h3 className={`text-base md:text-xl font-bold mb-2 md:mb-3 group-hover:text-[#c9a962] transition-colors line-clamp-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {car.name}
      </h3>
      
      {/* Car Features */}
      <div className="flex flex-wrap gap-1.5 md:gap-3 mb-3 md:mb-4">
        <span className={`inline-flex items-center gap-1 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
          <Icons.Car />
          {car.transmission}
        </span>
        <span className={`inline-flex items-center gap-1 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
          <Icons.Shield />
          {car.fuel_type}
        </span>
        <span className={`inline-flex items-center gap-1 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
          {car.seating_capacity} Seats
        </span>
      </div>
      
      {/* Description */}
      <p className={`text-xs md:text-sm mb-3 md:mb-4 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
        {car.description}
      </p>
      
      {/* Action Button */}
      <a 
        href="tel:9602360369"
        className="w-full flex items-center justify-center gap-2 py-2.5 md:py-3 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black text-sm md:text-base font-semibold uppercase tracking-wider hover:shadow-lg hover:shadow-[#c9a962]/30 transition-all duration-300 rounded-lg"
      >
        Book Now <Icons.ArrowRight />
      </a>
    </div>
    
    {/* Hover Glow Effect */}
    <div className="absolute inset-0 rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
      <div className="absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-br from-[#c9a962]/10 to-transparent"></div>
    </div>
  </div>
);

// Featured Car Card Component
export const FeaturedCarCard = ({ car, isDark, openBookingModal }) => (
  <div className={`relative overflow-hidden rounded-xl md:rounded-2xl transition-all duration-500 group hover:shadow-2xl hover:shadow-[#c9a962]/20 ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
    {/* Image Section */}
    <div className="relative h-44 sm:h-48 md:h-56 overflow-hidden">
      <img 
        src={car.image} 
        alt={car.name}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent opacity-80"></div>
      
      {/* Featured Badge */}
      <div className="absolute top-2 md:top-4 left-2 md:left-4 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black text-[10px] md:text-xs font-bold px-2 md:px-3 py-0.5 md:py-1 uppercase tracking-wider rounded-full">
        Featured
      </div>
      
      {/* Price Badge */}
      <div className="absolute bottom-2 md:bottom-4 right-2 md:right-4 bg-black/80 backdrop-blur-sm text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg">
        <span className="text-base md:text-xl font-bold text-[#c9a962]">₹{car.price_per_day?.toLocaleString()}</span>
        <span className="text-[10px] md:text-sm text-gray-400">/day</span>
      </div>
    </div>
    
    {/* Content Section */}
    <div className="p-4 md:p-6">
      <h3 className={`text-base md:text-xl font-bold mb-2 md:mb-3 group-hover:text-[#c9a962] transition-colors line-clamp-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {car.name}
      </h3>
      
      {/* Car Features */}
      <div className="flex flex-wrap gap-1.5 md:gap-3 mb-3 md:mb-4">
        <span className={`inline-flex items-center gap-1 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
          <Icons.Car />
          {car.transmission}
        </span>
        <span className={`inline-flex items-center gap-1 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
          <Icons.Shield />
          {car.fuel_type}
        </span>
        <span className={`inline-flex items-center gap-1 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
          {car.seating_capacity} Seats
        </span>
      </div>
      
      {/* Description */}
      <p className={`text-xs md:text-sm mb-3 md:mb-4 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
        {car.description}
      </p>
      
      {/* Action Button */}
      <button 
        onClick={() => openBookingModal(car)}
        className="w-full flex items-center justify-center gap-2 py-2.5 md:py-3 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black text-sm md:text-base font-semibold uppercase tracking-wider hover:shadow-lg hover:shadow-[#c9a962]/30 transition-all duration-300 rounded-lg cursor-pointer"
      >
        Book Now <Icons.ArrowRight />
      </button>
    </div>
    
    {/* Hover Glow Effect */}
    <div className="absolute inset-0 rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
      <div className="absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-br from-[#c9a962]/10 to-transparent"></div>
    </div>
  </div>
);

// Car Card for Luxury Cars Page
const CarCard = ({ car, isDark, openBookingModal }) => (
  <div className={`group overflow-hidden card-hover hover:scale-105 transition-transform duration-300 rounded-xl ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
    <div className="relative h-44 sm:h-48 md:h-56 overflow-hidden">
      <img src={car.image} alt={car.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent opacity-60"></div>
      {car.is_featured === 1 && <div className="absolute top-2 md:top-4 right-2 md:right-4 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black text-[10px] md:text-xs font-bold px-2 md:px-3 py-0.5 md:py-1 uppercase tracking-wider animate-pulse rounded-full">Featured</div>}
    </div>
    <div className={`p-4 md:p-6 ${isDark ? '' : 'bg-white'}`}>
      <h3 className={`text-base md:text-xl font-bold mb-3 md:mb-4 group-hover:text-[#c9a962] transition-colors line-clamp-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{car.name}</h3>
      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-3 md:mb-4">
        <div className={`flex items-center gap-1 md:gap-2 text-[10px] md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}><Icons.Car /><span className="truncate">{car.transmission}</span></div>
        <div className={`flex items-center gap-1 md:gap-2 text-[10px] md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}><Icons.Dollar /><span className="truncate">{car.fuel_type}</span></div>
        <div className={`flex items-center gap-1 md:gap-2 text-[10px] md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}><Icons.Shield /><span className="truncate">{car.seating_capacity} Seats</span></div>
      </div>
      <p className={`text-xs md:text-sm mb-3 md:mb-4 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{car.description}</p>
      <div className={`flex items-center justify-between pt-3 md:pt-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div>
          <span className="text-lg md:text-2xl font-bold text-[#c9a962]">₹{car.price_per_day ? car.price_per_day.toLocaleString() : car.price_per_day}</span>
          <span className={`text-[10px] md:text-sm ml-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>/day</span>
        </div>
        <button 
          onClick={() => openBookingModal(car)}
          className="px-3 md:px-5 py-1.5 md:py-2 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black text-[10px] md:text-sm font-semibold uppercase tracking-wider hover:shadow-lg hover:shadow-[#c9a962]/30 transition-all duration-300 rounded-lg"
        >
          Book Now
        </button>
      </div>
    </div>
  </div>
);

export default CarCard;
